import { Tooltip, TooltipContent, TooltipTrigger } from "@dune/ui";
import { DownloadCloud01, Upload01 } from "@untitled-ui/icons-react";
import DOMPurify from "dompurify";
import { Paperclip } from "lucide-react";
import React from "react";

const Avatar = ({
	className,
	children,
}: {
	className?: string;
	children: React.ReactNode;
}) => (
	<div
		className={`flex items-center justify-center rounded-full ${className || ""}`}
	>
		{children}
	</div>
);

const AvatarFallback = ({
	className,
	children,
}: {
	className?: string;
	children: React.ReactNode;
}) => (
	<div className={`flex items-center justify-center ${className || ""}`}>
		{children}
	</div>
);

export interface EmailViewerProps {
	from: string;
	to: string;
	date: string;
	content: string;
	brokerId?: number;
	emailId?: string;
	onImportEmail?: (emailAttachmentId: number, brokerId: number) => void;
	attachments?: Array<{
		id: number;
		fileName: string;
		contentType: string;
		size: number;
		url: string;
	}>;
}

const extractNameAndEmail = (
	emailString: string,
): { name: string; email: string } => {
	// Try to match the pattern "Name <email@example.com>"
	const match = emailString.match(/^(.+?)\s*(?:<(.+?)>)?$/);

	if (match?.[1] && match?.[2]) {
		return { name: match[1].trim(), email: match[2].trim() };
	}

	return { name: emailString, email: emailString };
};

const getInitials = (name: string): string => {
	return name
		.split(" ")
		.filter((part) => part.length > 0)
		.map((part) => part.charAt(0))
		.join("")
		.toUpperCase()
		.substring(0, 2);
};

export function EmailViewer({
	from,
	to,
	date,
	content,
	brokerId,
	emailId,
	onImportEmail,
	attachments,
}: EmailViewerProps) {
	const { name: fromName } = extractNameAndEmail(from);
	const { name: toName } = extractNameAndEmail(to);
	const fromInitials = getInitials(fromName);
	const toInitials = getInitials(toName);

	const sanitizedContent = DOMPurify.sanitize(content, {
		USE_PROFILES: { html: true },
	});

	const formatFileSize = (bytes: number): string => {
		if (bytes < 1024) return `${bytes} B`;
		if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
		return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
	};

	const handleImportEmail = (emailAttachmentId: number) => {
		if (brokerId && onImportEmail) {
			onImportEmail(emailAttachmentId, brokerId);
		}
	};

	return (
		<div className="h-full flex flex-col">
			<div className="border-b pb-4">
				<div className="grid grid-cols-[120px_1fr] gap-y-4">
					{/* From section */}
					<div className="text-xs text-gray-500">De</div>
					<div className="flex items-center">
						<Avatar className="h-8 w-8 bg-blue-100 mr-2 shrink-0">
							<AvatarFallback className="text-blue-600 text-xs shrink-0">
								{fromInitials}
							</AvatarFallback>
						</Avatar>
						<div className="flex flex-col">
							<span className="font-medium text-sm">{fromName}</span>
						</div>
					</div>

					{/* To section */}
					<div className="text-xs text-gray-500">A</div>
					<div className="flex items-center">
						<Avatar className="h-8 w-8 bg-blue-100 mr-2 shrink-0">
							<AvatarFallback className="text-blue-600 text-xs">
								{toInitials}
							</AvatarFallback>
						</Avatar>
						<div className="flex flex-col">
							<span className="font-medium text-sm">{toName}</span>
						</div>
					</div>

					{/* Date section */}
					<div className="text-xs text-gray-500">Date</div>
					<div className="text-sm">{date}</div>
				</div>
			</div>

			<div className="flex-1 py-4 overflow-auto">
				<div
					className="text-gray-800 text-sm whitespace-pre-line prose"
					// biome-ignore lint/security/noDangerouslySetInnerHtml: content sanitized with DOMPurify
					dangerouslySetInnerHTML={{ __html: sanitizedContent }}
				/>
			</div>

			{/* Attachments */}
			{attachments && attachments.length > 0 && (
				<div className="border-t pt-4">
					<div className="font-medium text-sm mb-2">Pièces jointes</div>
					<div className="space-y-2">
						{attachments.map((att) => (
							<div
								key={att.fileName}
								className="flex items-center justify-between p-2 border rounded-md bg-gray-50"
							>
								<div className="flex items-center">
									<Paperclip className="w-4 h-4 text-gray-500 mr-2" />
									<div>
										<p className="text-sm font-medium">{att.fileName}</p>
										<p className="text-xs text-gray-500">
											{formatFileSize(att.size)}
										</p>
									</div>
								</div>
								{att.url && (
									<div className="flex items-center gap-2">
										<Tooltip>
											<TooltipTrigger asChild>
												<button
													type="button"
													onClick={async () => {
														try {
															const response = await fetch(att.url);
															const blob = await response.blob();
															const blobUrl = URL.createObjectURL(blob);
															const link = document.createElement("a");
															link.href = blobUrl;
															link.download = att.fileName;
															document.body.appendChild(link);
															link.click();
															document.body.removeChild(link);
															URL.revokeObjectURL(blobUrl);
														} catch (err) {
															console.error(
																"Erreur lors du téléchargement :",
																err,
															);
														}
													}}
													className="flex items-center justify-center w-8 h-8 rounded-md bg-blue-50 hover:bg-blue-100 text-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
													aria-label={`Télécharger ${att.fileName}`}
												>
													<DownloadCloud01 className="w-4 h-4" />
												</button>
											</TooltipTrigger>
											<TooltipContent>Télécharger</TooltipContent>
										</Tooltip>
										{brokerId && emailId && onImportEmail && (
											<Tooltip>
												<TooltipTrigger asChild>
													<button
														type="button"
														onClick={() => handleImportEmail(att.id)}
														className="flex items-center justify-center w-8 h-8 rounded-md bg-green-50 hover:bg-green-100 text-green-600 transition-colors focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-2"
														aria-label="Importer dans les documents du courtier"
													>
														<Upload01 className="w-4 h-4" />
													</button>
												</TooltipTrigger>
												<TooltipContent>Importer</TooltipContent>
											</Tooltip>
										)}
									</div>
								)}
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
}
